import { DateFormat } from './DateField.types'

interface FormatPart {
  length: number
  maxValue?: number
  autoCompleteThreshold?: number
}

interface ParsedDateFormat {
  separator: string
  parts: FormatPart[]
  partPatterns: string[]
}

interface DateChunk {
  value: string
  splitFromOverflow: boolean
  completedByInputSeparator: boolean
}

interface AutoformatDateValueOptions {
  completePartWithInputSeparator?: boolean
}

const DAYS_PATTERN = 'dd'
const MONTHS_PATTERN = 'mm'
const SEPARATOR_REGEX = /[^a-z]/
const NON_DIGIT_REGEX = /\D/g
const REGEX_SPECIAL_CHARACTERS = /[.*+?^${}()|[\]\\]/g

const PART_CONFIG: Record<string, Pick<FormatPart, 'maxValue' | 'autoCompleteThreshold'>> = {
  [DAYS_PATTERN]: { maxValue: 31, autoCompleteThreshold: 3 },
  [MONTHS_PATTERN]: { maxValue: 12, autoCompleteThreshold: 1 },
}

const ALTERNATE_SEPARATORS = ['.', '/', '-']

const FORMAT_CACHE = new Map<DateFormat, ParsedDateFormat>()

function extractDigits(value: string): string {
  return value.replace(NON_DIGIT_REGEX, '')
}

export function autoformatDateValue(
  value: string,
  format: DateFormat,
  options: AutoformatDateValueOptions = {},
): string {
  const { separator, parts } = parseDateFormat(format)

  if (!extractDigits(value)) {
    return ''
  }

  const { chunks, inputSeparator } = splitIntoChunks(
    value,
    separator,
    parts,
  )
  const isAlternateSeparator = inputSeparator !== null && inputSeparator !== separator

  if (chunks.length === 0) {
    return ''
  }

  const inputParts = inputSeparator !== null ? value.split(inputSeparator) : []

  const filled = chunks.map(({ value: chunk, splitFromOverflow, completedByInputSeparator }, index) => {
    const part = parts[index]
    const isLastChunk = index === chunks.length - 1
    const normalizePastedPart = isAlternateSeparator && (!isLastChunk || chunk.length === part.length)
    const hasSubsequentInputSegments = inputParts
      .slice(index + 1)
      .some(part => extractDigits(part).length > 0)

    return fillPart(chunk, part, {
      completedByInputSeparator: completedByInputSeparator && options.completePartWithInputSeparator,
      normalizePastedPart,
      splitFromOverflow,
      deferThresholdPadding: index === 0 && hasSubsequentInputSegments,
    })
  })
  const joined = filled.join(separator)

  const lastFilled = filled[filled.length - 1]
  const lastFormat = parts[filled.length - 1]
  const isLastComplete = lastFilled.length === lastFormat.length
  const hasMoreParts = filled.length < parts.length
  const allPriorSegmentsComplete = filled
    .slice(0, -1)
    .every((part, index) => part.length === parts[index].length)
  const shouldAppendSeparator = hasMoreParts
    && (
      (isLastComplete && allPriorSegmentsComplete)
      || (!isLastComplete && filled.length === 1 && value.endsWith(separator))
    )

  if (shouldAppendSeparator) {
    return `${joined}${separator}`
  }

  return joined
}

export function buildPattern(format: DateFormat): string {
  const { separator, parts } = parseDateFormat(format)
  const escapedSeparator = separator.replace(REGEX_SPECIAL_CHARACTERS, '\\$&')
  const numericPartsPattern = parts
    .map(part => `\\d{${part.length}}`)
    .join(escapedSeparator)

  return `^${numericPartsPattern}$`
}

export function getDateSeparator(format: DateFormat): string {
  return parseDateFormat(format).separator
}

export function getFirstPartAutoCompleteThreshold(format: DateFormat): number | undefined {
  const { partPatterns } = parseDateFormat(format)

  return PART_CONFIG[partPatterns[0] ?? '']?.autoCompleteThreshold
}

export function getFirstPartPattern(format: DateFormat): string {
  const { partPatterns } = parseDateFormat(format)

  return partPatterns[0] ?? ''
}

interface SplitIntoChunksResult {
  chunks: DateChunk[]
  inputSeparator: string | null
}

function resolveInputSeparator(value: string, formatSeparator: string): string | null {
  if (formatSeparator && value.includes(formatSeparator)) {
    return formatSeparator
  }

  for (const alternateSeparator of ALTERNATE_SEPARATORS) {
    if (alternateSeparator !== formatSeparator && value.includes(alternateSeparator)) {
      return alternateSeparator
    }
  }

  return null
}

function splitIntoChunks(
  value: string,
  separator: string,
  parts: FormatPart[],
): SplitIntoChunksResult {
  const inputSeparator = resolveInputSeparator(value, separator)
  const chunks: DateChunk[] = []

  if (inputSeparator !== null) {
    const inputParts = value.split(inputSeparator)
    let carry = ''

    for (let index = 0; index < parts.length; index += 1) {
      const part = parts[index]
      const inputPart = inputParts[index]
      const source = extractDigits(`${carry}${inputPart ?? ''}`)

      if (!source) {
        break
      }

      const chunk = source.slice(0, part.length)
      carry = source.slice(part.length)
      const hasEmptyPartAfterChunk = index + 1 < inputParts.length && inputParts[index + 1] === ''
      const completedByInputSeparator = chunk.length < part.length
        && chunk.length > 0
        && hasEmptyPartAfterChunk
        && value.endsWith(inputSeparator)

      chunks.push({ value: chunk, splitFromOverflow: false, completedByInputSeparator })
    }

    return { chunks, inputSeparator }
  }

  const digits = extractDigits(value)
  let position = 0

  for (let partIndex = 0; partIndex < parts.length; partIndex += 1) {
    const part = parts[partIndex]

    if (position >= digits.length) {
      break
    }

    let chunk = digits.slice(position, position + part.length)
    let consumedLength = chunk.length

    const splitFromOverflow = (
      chunk.length === part.length
      && part.maxValue !== undefined
      && Number.parseInt(chunk, 10) > part.maxValue
    )

    if (splitFromOverflow) {
      chunk = digits.slice(position, position + 1)
      consumedLength = 1
    }

    chunks.push({ value: chunk, splitFromOverflow, completedByInputSeparator: false })
    position += consumedLength
  }

  return { chunks, inputSeparator: null }
}

interface FillPartOptions {
  completedByInputSeparator?: boolean
  normalizePastedPart?: boolean
  splitFromOverflow?: boolean
  deferThresholdPadding?: boolean
}

function fillPart(chunk: string, part: FormatPart, options: FillPartOptions = {}): string {
  const numericValue = Number.parseInt(chunk, 10)

  if (chunk.length < part.length) {
    const exceedsThreshold = part.autoCompleteThreshold !== undefined
      && numericValue > part.autoCompleteThreshold

    const shouldPad = options.normalizePastedPart
      || (
        options.completedByInputSeparator
        && part.autoCompleteThreshold !== undefined
        && numericValue > 0
      )
      || (options.splitFromOverflow && numericValue > 0)
      || (exceedsThreshold && !options.deferThresholdPadding)

    return shouldPad ? chunk.padStart(part.length, '0') : chunk
  }

  const shouldClamp = part.maxValue !== undefined && numericValue > part.maxValue

  return shouldClamp ? String(part.maxValue) : chunk
}

function parseDateFormat(format: DateFormat): ParsedDateFormat {
  const cached = FORMAT_CACHE.get(format)

  if (cached) {
    return cached
  }

  const normalizedFormat = format.toLowerCase()
  const separator = normalizedFormat.match(SEPARATOR_REGEX)?.[0] ?? ''
  const partPatterns = normalizedFormat.split(SEPARATOR_REGEX)
  const parts = partPatterns.map((pattern) => {
    const config = PART_CONFIG[pattern]

    return {
      length: pattern.length,
      maxValue: config?.maxValue,
      autoCompleteThreshold: config?.autoCompleteThreshold,
    }
  })
  const parsedDateFormat = { separator, parts, partPatterns }

  FORMAT_CACHE.set(format, parsedDateFormat)

  return parsedDateFormat
}
